import { DateTime } from '@ksyos/date-time';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { ServiceType } from '@common/enums/serviceType';
import { formatString } from '@common/utils/formatString';
import { getDisplayName } from '@common/utils/getDisplayName';
import { serviceSupervisorAddEditSchema } from '@common/validation/serviceSupervisor';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { OverlapChecker } from '@frontend/utils/overlapChecker';
import { RateLimiter } from '@frontend/utils/rateLimiter';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { IBreadcrumbItem } from '../../elements/common';
import {
    CancelButton,
    DateInput,
    Dropdown,
    FormContainer,
    FormGroup,
    FormHeader,
    FormLabel,
    FormMessage,
    GroupedDropdown,
    Header,
    InputCheckbox,
    InputGroup,
    Loading,
    OverlapMessage,
    SaveSubmit,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { OrganizationSelection } from '../../elements/OrganizationSelection';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';
import { CompensationPayoutList } from '../compensationPayouts/CompensationPayoutList';

type ServiceSupervisorAddEditProps = IWithRouterProps;

interface IEmployment {
    emailAddress?: string;
    emailAddressAlt?: string;
    id: string;
    organization: {
        emailAddressRequestInbox?: string;
    };
    organizationId: string;
    practitionerEmailAddress?: string;
    practitionerId?: string;
}

interface IOrganization {
    emailAddressRequestInbox: string;
    id: string;
    name: string;
}

interface IPractitioner {
    emailAddress: string;
    id?: string;
    nameFamily: string;
    nameGiven: string;
    nameInfix: string;
}

interface IService {
    id: string;
    name: string;
    serviceType: string;
}

interface IPractitionerEmployment {
    emailAddress: string;
    id: string;
    organization: IOrganization;
    organizationId: string;
}

interface IOrganizationEmployment {
    emailAddress: string;
    id: string;
    organization: {
        emailAddressRequestInbox: string;
        id: string;
    };
    practitioner: {
        emailAddress: string;
        nameFamily: string;
        nameGiven: string;
        nameInfix: string;
    };
    practitionerId: string;
}

interface IServiceSupervisor {
    compensationPayOut: number | null;
    employmentId: string | null;
    endDate: string | null;
    organizationId: string | null;
    sendNotificationEmployment: boolean;
    sendNotificationEmploymentAlt: boolean;
    sendNotificationPractitioner: boolean;
    serviceId: string | null;
    startDate: string | null;
}

interface IServiceSupervisorsAddEditState {
    employment?: IEmployment;
    errors: ValidationErrors;
    isSaving: boolean;
    organizationEmployments: IOrganizationEmployment[];
    organizations: IOrganization[];
    overlap?: {
        endDate: string | null;
        id: string;
        startDate: string;
    };
    practitioner?: IPractitioner;
    practitionerEmployments: IPractitionerEmployment[];
    service?: IService;
    services: IService[];
    serviceSupervisor?: IServiceSupervisor;
}

const translate = getSelectedLanguage();
class InnerServiceSupervisorAddEdit extends Component<ServiceSupervisorAddEditProps, IServiceSupervisorsAddEditState> {
    private readonly atMostOncePerSecond = new RateLimiter(1000);
    private readonly overlapChecker = new OverlapChecker('/api/serviceSupervisors');

    constructor(props: ServiceSupervisorAddEditProps, context?: {}) {
        super(props, context);

        assertHasAuthorization(ManagerAuthorization.serviceRightsCareproviders);

        if (
            !(this.props.router.searchParams.get('practitionerId') || this.props.router.searchParams.get('serviceId'))
        ) {
            throw new Error('No practitionerId or serviceId');
        }

        this.state = {
            services: [],
            organizations: [],
            practitionerEmployments: [],
            organizationEmployments: [],
            isSaving: false,
            errors: emptyValidationErrors,
        };
    }

    public async componentDidMount() {
        await this.setupComponent(this.props);
    }

    public async UNSAFE_componentWillReceiveProps(newProps: ServiceSupervisorAddEditProps) {
        if (newProps.router.searchParams.get('practitionerId') || newProps.router.searchParams.get('serviceId')) {
            await this.setupComponent(newProps);
        } else {
            throw new Error('No practitionerId or serviceId');
        }
    }

    public render() {
        const breadcrumbs: IBreadcrumbItem[] = [{ text: translate.supervisors.supervisors }];
        if (this.getAddOrEdit() === 'edit') {
            breadcrumbs.push({ text: translate.common.edit });
        } else {
            breadcrumbs.push({ text: translate.common.new });
        }
        if (this.state.practitioner) {
            breadcrumbs.push({
                text: getDisplayName(this.state.practitioner),
                url: `/practitioners/${this.state.practitioner.id}`,
            });
        }
        const header =
            (this.getAddOrEdit() === 'edit' ? translate.common.edit : translate.common.add) +
            ` ${translate.supervisors.supervisors} ${
                this.state.serviceSupervisor && this.state.service ? this.state.service.name : ''
            }`;

        return (
            <FixedWidthTemplate breadcrumb={breadcrumbs}>
                <Header content={header} />
                <Loading
                    item={this.state.serviceSupervisor}
                    loaded={(serviceSupervisor) => (
                        <FormContainer onSubmit={async () => await this.save()}>
                            {this.state.overlap && (
                                <OverlapMessage
                                    endDate={this.state.overlap.endDate}
                                    link={`/serviceSupervisors/${
                                        this.state.overlap.id
                                    }?practitionerId=${this.getPractitionerId()}&returnTo=
                                    details&${
                                        this.props.router.searchParams.get('practitionerId')
                                            ? `practitionerDetailsId=${this.getPractitionerId()}`
                                            : `serviceDetailsId=${this.getServiceId()}`
                                    }`}
                                    message={translate.supervisors.overlapMessage}
                                    startDate={this.state.overlap.startDate}
                                />
                            )}
                            <FormGroup hasErrors={this.state.errors.has('startDate')}>
                                <FormLabel isRequired>{translate.common.startDate}</FormLabel>
                                <InputGroup>
                                    <DateInput
                                        error={this.state.errors.get('startDate')}
                                        onChange={async (value) => await this.handleChange('startDate', value)}
                                        onDoValidate={async () => this.validateOne('startDate')}
                                        value={serviceSupervisor.startDate}
                                    />
                                </InputGroup>
                            </FormGroup>
                            <FormGroup hasErrors={this.state.errors.has('endDate')}>
                                <FormLabel>{translate.common.endDate}</FormLabel>
                                <InputGroup>
                                    <DateInput
                                        error={this.state.errors.get('endDate')}
                                        onChange={async (value) => await this.handleChange('endDate', value)}
                                        onDoValidate={async () => this.validateOne('endDate')}
                                        value={serviceSupervisor.endDate}
                                    />
                                </InputGroup>
                            </FormGroup>
                            {this.getAddOrEdit() === 'add' && this.props.router.searchParams.get('practitionerId') && (
                                <>
                                    <FormHeader>
                                        {translate.employment.employment} & {translate.service.service}
                                    </FormHeader>
                                    <FormGroup hasErrors={this.state.errors.has('employmentId')}>
                                        <FormLabel>{translate.employment.employment}</FormLabel>
                                        <InputGroup>
                                            <Dropdown
                                                error={this.state.errors.get('employmentId')}
                                                onChange={async (value) =>
                                                    await this.handleChange('employmentId', value)
                                                }
                                                onDoValidate={async () => await this.validateOne('employmentId')}
                                                selectOptions={[
                                                    {
                                                        value: '',
                                                        text: formatString(translate.common.selectItem, [
                                                            translate.employment.employment,
                                                        ]),
                                                    },
                                                ].concat(
                                                    this.state.practitionerEmployments.map(
                                                        (practitionerEmployment) => ({
                                                            value: practitionerEmployment.id,
                                                            text: practitionerEmployment.organization.name,
                                                        })
                                                    )
                                                )}
                                                value={serviceSupervisor.employmentId}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                    {this.state.services && (
                                        <FormGroup hasErrors={this.state.errors.has('serviceId')}>
                                            <FormLabel>{translate.service.service}</FormLabel>
                                            <InputGroup>
                                                <GroupedDropdown
                                                    error={this.state.errors.get('serviceId')}
                                                    onChange={async (value) =>
                                                        await this.handleChange('serviceId', value)
                                                    }
                                                    onDoValidate={async () => await this.validateOne('serviceId')}
                                                    selectOptions={[
                                                        {
                                                            value: '',
                                                            text: formatString(translate.common.selectItem, [
                                                                translate.service.service,
                                                            ]),
                                                            group: '',
                                                        },
                                                    ].concat(
                                                        this.state.services.map((service) => ({
                                                            value: service.id,
                                                            text: service.name,
                                                            group: service.serviceType,
                                                        }))
                                                    )}
                                                    value={serviceSupervisor.serviceId}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    )}
                                </>
                            )}
                            {this.getAddOrEdit() === 'add' && this.props.router.searchParams.get('serviceId') && (
                                <>
                                    <FormHeader>
                                        {translate.organization.organization} & {translate.practitioner.practitioner}
                                    </FormHeader>
                                    <FormGroup hasErrors={this.state.errors.has('organizationId')}>
                                        <FormLabel>{translate.organization.organization}</FormLabel>
                                        <InputGroup>
                                            <OrganizationSelection
                                                error={this.state.errors.get('organizationId')}
                                                isKsyosOrganizationExcluded={true}
                                                onChange={async (value) =>
                                                    await this.handleChange('organizationId', value || null)
                                                }
                                                onDoValidate={async () => await this.validateOne('organizationId')}
                                                placeholder={formatString(translate.common.selectItem, [
                                                    translate.organization.organization,
                                                ])}
                                                value={serviceSupervisor.organizationId ?? undefined}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                    <FormGroup hasErrors={this.state.errors.has('employmentId')}>
                                        <FormLabel>{translate.practitioner.practitioner}</FormLabel>
                                        <InputGroup>
                                            <Dropdown
                                                error={this.state.errors.get('employmentId')}
                                                onChange={async (value) =>
                                                    await this.handleChange('employmentId', value)
                                                }
                                                onDoValidate={async () => await this.validateOne('employmentId')}
                                                selectOptions={[
                                                    {
                                                        value: '',
                                                        text: formatString(translate.common.selectItem, [
                                                            translate.practitioner.practitioner,
                                                        ]),
                                                    },
                                                ].concat(
                                                    this.state.organizationEmployments.map(
                                                        (organizationEmployment) => ({
                                                            value: organizationEmployment.id,
                                                            text: getDisplayName(organizationEmployment.practitioner),
                                                        })
                                                    )
                                                )}
                                                value={serviceSupervisor.employmentId}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                </>
                            )}

                            {this.props.router.params.id && (
                                <CompensationPayoutList
                                    practitionerId={this.props.router.searchParams.getOptional('practitionerId')}
                                    serviceId={this.props.router.searchParams.getOptional('serviceId')}
                                    serviceSupervisorId={this.props.router.params.id}
                                />
                            )}
                            {this.state.serviceSupervisor && (
                                <>
                                    <FormHeader> {translate.common.notifications} </FormHeader>
                                    <FormGroup hasErrors={this.state.errors.has('sendNotificationPractitioner')}>
                                        <InputGroup>
                                            <InputCheckbox
                                                checked={serviceSupervisor.sendNotificationPractitioner}
                                                error={this.state.errors.get('sendNotificationPractitioner')}
                                                onChange={async (value) =>
                                                    this.handleChange('sendNotificationPractitioner', value)
                                                }
                                                onDoValidate={async () =>
                                                    this.validateOne('sendNotificationPractitioner')
                                                }
                                                text={this.getPractitionerNotificationMessage()}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                    <FormGroup hasErrors={this.state.errors.has('sendNotificationEmployment')}>
                                        <InputGroup>
                                            <InputCheckbox
                                                checked={serviceSupervisor.sendNotificationEmployment}
                                                error={this.state.errors.get('sendNotificationEmployment')}
                                                onChange={async (value) =>
                                                    this.handleChange('sendNotificationEmployment', value)
                                                }
                                                onDoValidate={async () =>
                                                    this.validateOne('sendNotificationEmployment')
                                                }
                                                text={this.getEmploymentNotificationMessage(false)}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                    <FormGroup hasErrors={this.state.errors.has('sendNotificationEmploymentAlt')}>
                                        <InputGroup>
                                            <InputCheckbox
                                                checked={serviceSupervisor.sendNotificationEmploymentAlt}
                                                error={this.state.errors.get('sendNotificationEmploymentAlt')}
                                                onChange={async (value) =>
                                                    this.handleChange('sendNotificationEmploymentAlt', value)
                                                }
                                                onDoValidate={async () =>
                                                    this.validateOne('sendNotificationEmploymentAlt')
                                                }
                                                text={this.getEmploymentNotificationMessage(true)}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                    <FormMessage
                                        text={
                                            translate.supervisors.checkIfEmptyOrganizationEmailField +
                                            '(' +
                                            this.renderOrganizationEmail() +
                                            ')'
                                        }
                                    />
                                </>
                            )}
                            <CancelButton
                                disabled={this.state.isSaving}
                                to={this.returnTo()}
                            />
                            <SaveSubmit disabled={this.state.isSaving || !!this.state.overlap} />
                        </FormContainer>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private async setupComponent(props: ServiceSupervisorAddEditProps) {
        const practitionerId = this.props.router.searchParams.get('practitionerId');
        const serviceId = this.props.router.searchParams.get('serviceId');
        if (this.getAddOrEdit() === 'add') {
            await this.setEmptyServiceSupervisor();
        }
        if (practitionerId) {
            await this.loadPractitioner(practitionerId);
            if (this.getAddOrEdit(props.router.params) === 'edit') {
                await this.load(props.router.params.id!);
            } else {
                await this.loadPractitionerEmployments(practitionerId);
                await this.loadAllServices();
            }
        } else if (serviceId) {
            if (this.getAddOrEdit(props.router.params) === 'edit') {
                await this.load(props.router.params.id!);
            } else {
                await this.loadAllOrganizations();
            }
            await this.loadService(serviceId);
        }
    }

    private async setEmptyServiceSupervisor() {
        await this.setState({
            serviceSupervisor: {
                startDate: DateTime.now().format('YYYY-MM-DD'),
                endDate: null,
                compensationPayOut: null,
                employmentId: null,
                organizationId: null,
                serviceId: null,
                sendNotificationEmploymentAlt: false,
                sendNotificationEmployment: false,
                sendNotificationPractitioner: false,
            },
        });
    }

    private async handleChange<K extends keyof IServiceSupervisor>(name: K, value: IServiceSupervisor[K]) {
        await this.setState(({ serviceSupervisor }) => ({
            serviceSupervisor: { ...serviceSupervisor, [name]: value },
        }));
        if (
            name === 'employmentId' ||
            name === 'serviceId' ||
            name === 'startDate' ||
            name === 'endDate' ||
            name === 'organizationId'
        ) {
            this.checkOverlap();
        }
        if (name === 'employmentId' && this.state.practitionerEmployments) {
            await this.setState({ employment: undefined });
            await this.setPractitionerEmploymentObject();
        }
        if (name === 'employmentId' && this.state.organizationEmployments) {
            await this.setOrganizationEmploymentObject();
        }
        if (name === 'organizationId') {
            await this.setState({ employment: undefined });
            if (value) {
                this.loadOrganizationEmployments();
            } else {
                await this.setState({ organizationEmployments: [] });
            }
        }
    }

    private async addValidationForOrganizationId(errors: ValidationErrors) {
        if (this.state.serviceSupervisor && !this.state.serviceSupervisor.organizationId) {
            return errors.set('organizationId', 'string_base');
        }

        return errors;
    }

    private async validateOne(name: keyof IServiceSupervisor) {
        await this.setState(({ serviceSupervisor, errors }) => ({
            errors: updateValidationErrorsForOne(serviceSupervisorAddEditSchema, serviceSupervisor, errors, name),
        }));
    }

    private async checkOverlap() {
        const serviceId = this.getServiceId();
        if (serviceId) {
            this.atMostOncePerSecond.callMeMaybe(async () => {
                if (this.state.serviceSupervisor) {
                    const overlap = await this.overlapChecker.check(
                        { employmentId: this.state.serviceSupervisor.employmentId, serviceId: serviceId },
                        this.state.serviceSupervisor.startDate,
                        this.state.serviceSupervisor.endDate,
                        this.props.router.params.id
                    );
                    await this.setState({ overlap });
                }
            });
        }
    }

    private getPractitionerId() {
        const practitionerId = this.props.router.searchParams.get('practitionerId');
        if (practitionerId) {
            return practitionerId;
        } else if (this.state.employment && this.state.employment.practitionerId) {
            return this.state.employment.practitionerId;
        } else if (this.state.serviceSupervisor) {
            const organizationEmployment = this.state.organizationEmployments.find(
                (employment) => employment.id === this.state.serviceSupervisor!.employmentId
            );
            if (organizationEmployment) {
                return organizationEmployment.practitionerId;
            }
        } else {
            return undefined;
        }
    }

    private getServiceId() {
        const serviceId = this.props.router.searchParams.get('serviceId');
        if (serviceId) {
            return serviceId;
        } else if (this.state.serviceSupervisor && this.state.serviceSupervisor.serviceId) {
            return this.state.serviceSupervisor.serviceId;
        } else {
            return undefined;
        }
    }

    private getAddOrEdit(paramsArgument?: { id?: string }) {
        const params = paramsArgument || this.props.router.params;

        return params.id ? 'edit' : 'add';
    }

    private async setPractitionerEmploymentObject() {
        if (this.state.serviceSupervisor) {
            const selectedEmployment = this.state.practitionerEmployments.find(
                (practitionerEmployment) => practitionerEmployment.id === this.state.serviceSupervisor!.employmentId
            );
            if (selectedEmployment && this.state.serviceSupervisor.employmentId && this.state.practitioner) {
                const employment: IEmployment = {
                    id: this.state.serviceSupervisor.employmentId,
                    emailAddress: selectedEmployment.emailAddress,
                    organization: {
                        emailAddressRequestInbox: selectedEmployment.organization.emailAddressRequestInbox,
                    },
                    organizationId: selectedEmployment.organizationId,
                    practitionerEmailAddress: this.state.practitioner.emailAddress,
                };
                await this.setState({ employment });
            }
        }
    }

    private async setOrganizationEmploymentObject() {
        if (this.state.serviceSupervisor) {
            const selectedEmployment = this.state.organizationEmployments.find(
                (organizationEmployment) => organizationEmployment.id === this.state.serviceSupervisor!.employmentId
            );
            if (
                selectedEmployment &&
                this.state.serviceSupervisor.organizationId &&
                this.state.serviceSupervisor.employmentId
            ) {
                const employment: IEmployment = {
                    id: this.state.serviceSupervisor.employmentId,
                    emailAddress: selectedEmployment.emailAddress,
                    organization: {
                        emailAddressRequestInbox: selectedEmployment.organization.emailAddressRequestInbox,
                    },
                    organizationId: this.state.serviceSupervisor.organizationId,
                    practitionerEmailAddress: selectedEmployment.practitioner.emailAddress,
                };
                await this.setState({ employment });
            }
        }
    }

    private getPractitionerNotificationMessage() {
        const message = translate.supervisors.sendNotificationPractitioner;
        let email = '';
        if (this.state.practitioner) {
            if (this.state.practitioner.emailAddress) {
                email = this.state.practitioner.emailAddress;
            } else {
                email = translate.common.noEmailConfigured;
            }
        } else if (this.state.employment) {
            if (this.state.employment.practitionerEmailAddress) {
                email = this.state.employment.practitionerEmailAddress;
            } else {
                email = translate.common.noEmailConfigured;
            }
        }

        return email ? `${message} (${email})` : message;
    }

    private getEmploymentNotificationMessage(useEmailAddressAlt: boolean) {
        let message: string;
        if (this.state.employment) {
            if (this.state.employment.emailAddress && !useEmailAddressAlt) {
                message = this.state.employment.emailAddress;
            } else if (this.state.employment.emailAddressAlt && useEmailAddressAlt) {
                message = this.state.employment.emailAddressAlt;
            } else {
                message = translate.common.noEmailConfigured;
            }
        } else {
            message = translate.supervisors.noEmploymentChosen;
        }
        const label = useEmailAddressAlt
            ? translate.serviceProvider.sendNotificationEmploymentAlt
            : translate.serviceProvider.sendNotificationEmployment;

        return label + ' (' + message + ')';
    }

    private renderOrganizationEmail() {
        if (this.state.employment && this.state.employment.organization) {
            if (this.state.employment.organization.emailAddressRequestInbox) {
                return this.state.employment.organization.emailAddressRequestInbox;
            } else {
                return translate.supervisors.emptyGenericOrganizationEmailField;
            }
        } else {
            return translate.supervisors.noEmploymentChosen;
        }
    }

    private async loadPractitioner(practitionerId: string) {
        const url = `/api/practitioners/${practitionerId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ practitioner: result.json });
    }

    private async loadService(serviceId: string) {
        const url = `/api/services/${serviceId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ service: result.json });
    }

    private async loadAllOrganizations() {
        const url = '/api/organizations/list';
        const results = await fetchJson(url, 'POST');
        if (results.json.items.length > 1) {
            await this.setState({ organizations: results.json.items });
        } else if (results.json.items.length === 1) {
            await this.setState(({ serviceSupervisor }) => ({
                serviceSupervisor: { ...serviceSupervisor, organizationId: results.json.items[0].id },
                organizations: results.json.items,
            }));
        }
    }

    private async loadOrganizationEmployments() {
        if (this.state.serviceSupervisor) {
            const url = `/api/employments/${this.state.serviceSupervisor.organizationId}/practitioners?isActive=true&isNoManager=true`;
            const results = await fetchJson(url, 'GET');
            if (results.json.items.length > 1 || results.json.items.length === 0) {
                await this.setState(({ serviceSupervisor }) => ({
                    serviceSupervisor: { ...serviceSupervisor, employmentId: undefined },
                    organizationEmployments: results.json.items,
                }));
            } else if (results.json.items.length === 1) {
                await this.setState(({ serviceSupervisor }) => ({
                    serviceSupervisor: { ...serviceSupervisor, employmentId: results.json.items[0].id },
                    organizationEmployments: results.json.items,
                }));
                await this.setOrganizationEmploymentObject();
                await this.checkOverlap();
            }
        }
    }

    private async loadPractitionerEmployments(practitionerId: string) {
        const url = `/api/employments/${practitionerId}/organizations?isActive=true&isNoManager=true`;
        const results = await fetchJson(url, 'GET');
        if (results.json.items.length > 1) {
            await this.setState({ practitionerEmployments: results.json.items });
        } else if (results.json.items.length === 1) {
            await this.setState(({ serviceSupervisor }) => ({
                serviceSupervisor: {
                    ...serviceSupervisor,
                    employmentId: results.json.items[0].id,
                },
                practitionerEmployments: results.json.items,
            }));

            await this.setPractitionerEmploymentObject();
        }
    }

    private async loadAllServices() {
        const url = `/api/services?selectionList=true&serviceIsActive=true&servicetype=${ServiceType.examination}`;
        const results = await fetchJson(url, 'GET');
        if (results.json.items.length > 1) {
            await this.setState({ services: results.json.items });
        } else if (results.json.items.length === 1) {
            await this.setState(({ serviceSupervisor }) => ({
                serviceSupervisor: { ...serviceSupervisor, serviceId: results.json.items[0].id },
                services: results.json.items,
            }));
        }
    }

    private returnTo() {
        const returnTo = this.props.router.searchParams.get('returnTo');
        const practitionerDetailsId = this.props.router.searchParams.get('practitionerDetailsId');
        switch (returnTo) {
            case null:
                return '/patients';
            case 'practitioner':
                return `/practitioners/${this.getPractitionerId()}?tab=serviceSupervisorsListIsVisible`;
            case 'services':
                return `/services/${this.getServiceId()}?tab=supervisors`;
            case 'details':
                if (practitionerDetailsId) {
                    return `/practitioners/${practitionerDetailsId}?tab=serviceSupervisorsListIsVisible`;
                } else {
                    return `/services/${this.props.router.searchParams.get('serviceDetailsId')}?tab=supervisors`;
                }
            default:
                throw new Error(`Unknown returnTo: "${returnTo}"`);
        }
    }

    private async load(serviceSupervisorId: string) {
        const url = `/api/serviceSupervisors/${serviceSupervisorId}`;
        const results = await fetchJson(url, 'GET');
        await this.setState({
            serviceSupervisor: results.json,
            employment: results.json.employment,
            overlap: undefined,
        });
    }

    private async save() {
        if (this.state.overlap) {
            return;
        }
        let url = '/api/serviceSupervisors/';
        if (this.getAddOrEdit() === 'edit') {
            url += this.props.router.params.id;
        }
        if (this.getAddOrEdit() === 'add' && this.props.router.searchParams.get('serviceId')) {
            const serviceId = this.getServiceId();
            await this.setState(({ serviceSupervisor }) => ({
                serviceSupervisor: { ...serviceSupervisor, serviceId: serviceId },
            }));
        }
        const validation = validate(serviceSupervisorAddEditSchema, this.state.serviceSupervisor);
        let { errors } = validation;
        const { item } = validation;
        if (this.getAddOrEdit() === 'add' && this.props.router.searchParams.get('serviceId')) {
            errors = await this.addValidationForOrganizationId(errors);
        }
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }
        await this.setState({ isSaving: true });
        await fetchJson(url, 'POST', item);
        this.props.router.navigate(this.returnTo());
    }
}

export const ServiceSupervisorAddEdit = withRouter(InnerServiceSupervisorAddEdit);
